// ============================================================
// BattlefieldMeasureLine - the tape measure, drawn.
//
// A line between the two points with the distance on it, dashed red when
// something opaque stands in the way. The line is drawn through the AIR,
// at eye height, because that is the shot being measured - a line drawn
// along the ground would say a barricade blocks a shot that clears it.
// ============================================================
import { memo } from 'react';
import type { Camera } from '../engine/battlefield/projection';
import { project, SCENE_VIEW } from '../engine/battlefield/projection';
import type { Measurement } from '../engine/battlefield/measure';
import { formatMetres } from '../engine/battlefield/measure';
import type { Vec3 } from '../engine/battlefield/types';
import styles from '../styles/Battlefield.module.css';

interface MeasureLine {
  from: Vec3;
  to: Vec3;
  measurement: Measurement | null;
}

function BattlefieldMeasureLineImpl({ line, camera }: { line: MeasureLine | null; camera: Camera }) {
  if (!line) return null;
  const a = project(line.from, camera, SCENE_VIEW);
  const b = project(line.to, camera, SCENE_VIEW);
  const blocked = line.measurement?.blocked;
  const metres = line.measurement ? formatMetres(line.measurement.metres) : '';

  return (
    <g data-testid="measure-line" className={styles.measure}>
      {/* Feet-to-eye ticks: without them a line in the air is impossible
          to place on the ground at a glance. */}
      <line className={styles.measureDrop} x1={a.x} y1={a.y} x2={a.x} y2={a.y + 6} />
      <line
        data-testid="measure-segment"
        className={blocked ? styles.measureBlocked : styles.measureClear}
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
      />
      <circle className={styles.measureEnd} cx={a.x} cy={a.y} r={3} />
      <circle className={styles.measureEnd} cx={b.x} cy={b.y} r={3} />
      {metres && (
        <text className={styles.measureLabel} x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 8} textAnchor="middle">
          {metres}
        </text>
      )}
    </g>
  );
}

export default memo(BattlefieldMeasureLineImpl);
